"use client";

import { partnerHrefs } from "@/lib/usePartnerCode";
import { useActivePartnerCode } from "@/lib/useActivePartnerCode";
import Link from "@/components/LocaleLink";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "@/lib/i18n/LanguageContext";

/**
 * Routes where the dock must not render: focused form flows, upload and admin.
 * There its CTA is redundant and it can overlap form controls on mobile.
 */
const SUPPRESSED_PATHS = [
  "/anfrage",
  "/buchen",
  "/beratung",
  "/eignungs-check",
  // Has its own "book the change" buttons; the free-check bar would pull the
  // couples who already have their approval away from it (24.10.2026).
  "/gemeindewechsel",
  "/kontakt",
  // The withdrawal function: its confirm button must never sit under a sales bar.
  "/vertrag-widerrufen",
  "/status",
  "/admin",
];

/**
 * Whether the phone's check bar is left out on this page. Shared with the
 * footer, which only reserves room for the bar where it exists.
 */
export function isDockSuppressed(pathname: string | null): boolean {
  // Strip the /en and /da locale prefix, otherwise suppression only matches DE.
  const path = (pathname ?? "").replace(/^\/(en|da|ar|uk|ru|vi|es)(?=\/|$)/, "");
  return SUPPRESSED_PATHS.some((p) => path.startsWith(p));
}

/** The first half screen of a page: the page's own top is what a visitor
 * reads there. */
function subscribeScroll(onChange: () => void) {
  window.addEventListener("scroll", onChange, { passive: true });
  window.addEventListener("resize", onChange);
  return () => {
    window.removeEventListener("scroll", onChange);
    window.removeEventListener("resize", onChange);
  };
}
const isNearTop = () => window.scrollY < window.innerHeight * 0.5;

/**
 * The page's red buttons: the marked hero buttons, every red pill and the
 * phone's selected package tab (Complete, selected by default, is a filled red
 * tab). Only the pricing tabs (they control the pkg-card-* cards): the guide
 * filters are tabs too, but pale.
 */
const RED_BUTTONS =
  '[data-hero-cta], a.rounded-full.bg-coral-500, button.rounded-full.bg-coral-500, [role="tab"][aria-controls^="pkg-card-"][aria-selected="true"]';

/**
 * The phone's bar with the free check — the only floating element left. It
 * stays down while a red check button of the page itself is on screen (the
 * hero's, the closing section's — anything marked data-hero-cta), so the same
 * red button never shows twice; booking ("Jetzt buchen") sits in the menu and
 * on the pricing cards. On desktop the header carries WhatsApp, Telegram and
 * "Jetzt buchen" at all times, so no floating element is needed there.
 */
export function ConversionDock() {
  // Slides away on scroll down, returns on scroll up; otherwise it permanently
  // covers ~80px of content on every page.
  const [hidden, setHidden] = useState(false);
  // Whether the hero's own call to action is on screen, keyed by the page it
  // was measured on, so a stale value from the previous page never keeps the
  // bar down. Once the hero fits on one phone screen, the bar would otherwise
  // sit directly under the hero's red button — the same button twice.
  const [heroCta, setHeroCta] = useState<{ path: string | null; visible: boolean }>({
    path: null,
    visible: false,
  });
  const lastY = useRef(0);
  const { t } = useTranslation();
  const pathname = useSitePathname();
  const isSuppressed = isDockSuppressed(pathname);
  const partnerCode = useActivePartnerCode();
  const checkHref = partnerHrefs(partnerCode).check;
  const heroCtaSichtbar = heroCta.path === pathname && heroCta.visible;
  // Down on the first half screen of every page, so on a page without a red
  // hero button (guides, testimonials, legal) the bar never covers the end of
  // the page top on arrival; it comes up once the visitor is reading.
  const nearTop = useSyncExternalStore(subscribeScroll, isNearTop, () => true);

  useEffect(() => {
    // Every red pill of the page counts — the hero's and closing section's
    // (data-hero-cta), "Complete wählen" on the pricing cards, "Chat starten",
    // the check button under the comparison — so the bar never shows a second
    // red button on the same screen. Sections that load later (dynamic imports
    // on the home page) are picked up by the MutationObserver.
    const sichtbar = new Set<Element>();
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        for (const eintrag of eintraege) {
          if (eintrag.isIntersecting) sichtbar.add(eintrag.target);
          else sichtbar.delete(eintrag.target);
        }
        setHeroCta({ path: pathname, visible: sichtbar.size > 0 });
      },
      // The bar goes down just BEFORE a red button enters from below (the
      // viewport counts 140px further down), so a button coming into view is
      // never half hidden behind the bar — two red buttons stacked.
      { threshold: 0, rootMargin: "0px 0px 140px 0px" },
    );
    const beobachtet = new WeakSet<Element>();
    const erfasse = () => {
      document.querySelectorAll(RED_BUTTONS).forEach((ziel) => {
        if (beobachtet.has(ziel) || ziel.closest(".floating-cta")) return;
        beobachtet.add(ziel);
        beobachter.observe(ziel);
      });
    };
    erfasse();
    const neue = new MutationObserver(erfasse);
    // aria-selected too: a package tab picked later is observed as well.
    neue.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-selected"],
    });
    return () => {
      neue.disconnect();
      beobachter.disconnect();
    };
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY.current) > 24) {
        setHidden(y > lastY.current && y > 160);
        lastY.current = y;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isUp = !isSuppressed && !(hidden || nearTop || heroCtaSichtbar);
  // The cookie notice sits right above the bar while the bar is up and at the
  // bottom edge otherwise; with a fixed offset it floated over the hero's
  // price lines while the bar was down (journey test 04.10.2026).
  useEffect(() => {
    document.documentElement.classList.toggle("dock-up", isUp);
    return () => document.documentElement.classList.remove("dock-up");
  }, [isUp]);

  if (isSuppressed) return null;

  // Opaque, not translucent, so page text cannot show through. The bar must
  // be in the first paint so its link works before hydration; the entrance is
  // pure CSS (dock-rise with `both`), not a JS timer. No spacer needed: the
  // footer already reserves bottom padding.
  return (
    <div
      className={`floating-cta fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 sm:hidden dock-safe-bottom ${
        isUp ? "translate-y-0" : "translate-y-full"
      }`}
      style={{ animation: "dock-rise .45s ease-out .3s both" }}
    >
      <div className="border-t border-navy-100 bg-white px-3 py-2">
        <Link
          href={checkHref}
          data-track="dock_check"
          className="flex h-11 items-center justify-center gap-2 rounded-full bg-coral-500 px-4 text-[14.5px] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(200,16,46,0.7)] transition-transform active:scale-[0.98]"
        >
          {t("dock", "checkNow")}
          <ArrowRight className="h-[17px] w-[17px]" strokeWidth={2.2} aria-hidden />
        </Link>
      </div>
    </div>
  );
}
